import { getHighestNumber } from '@/util/number';
import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Semaphore } from '@/types/semaphores';
import { getUpdatedNodes } from '@/util/application';
import { Button, Stack } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { FC, useState } from 'react';
import SemaphoreListItem from './SemaphoreListItem';
import SemaphoreListItemEdit from './SemaphoreListItemEdit';

const SemaphoreManager: FC = () => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { confirm } = useDialogs();

  const [openEdit, setOpenEdit] = useState(false);

  const confirmExists: ConfirmOptions = {
    title: 'Event already exists',
    description: `This user event already exists`,
    confirmationText: 'Ok',
    hideCancelButton: true
  };

  const handleOnSave = (newSemaphore: Semaphore, oldName?: string) => {
    let updatedSemaphores = application.semaphores ? structuredClone(application.semaphores) : [];

    if (!oldName) {
      updatedSemaphores[updatedSemaphores.length] = {
        id: getHighestNumber(application.semaphores, 'id') + 1,
        name: newSemaphore.name,
        value: newSemaphore.value
      };
    } else {
      updatedSemaphores = updatedSemaphores.map((semaphore) => {
        if (semaphore.name === oldName) {
          return { id: semaphore.id, name: newSemaphore.name, value: newSemaphore.value };
        } else {
          return semaphore;
        }
      });
    }
    if (
      updatedSemaphores.filter(
        (semaphore) => semaphore.name.toLocaleLowerCase() === newSemaphore.name.toLocaleLowerCase()
      ).length > 1
    ) {
      confirm(confirmExists);
      return;
    }

    setApplication({ ...application, semaphores: updatedSemaphores });
    setUpdateNodes(getUpdatedNodes(newSemaphore.id, 'semaphore_id', application.blocks.details));
  };

  return (
    <Stack gap={1} alignItems='start'>
      <List sx={{ width: '100%' }}>
        {application.semaphores
          ?.sort((semaphoreA, semaphoreB) => {
            if (!semaphoreA.name && !semaphoreB.name) return 0;
            if (!semaphoreA.name) return 1;
            if (!semaphoreB.name) return -1;
            return semaphoreA.name.localeCompare(semaphoreB.name);
          })
          .map((semaphore, index) => {
            return (
              <SemaphoreListItem
                key={`prompt.${semaphore.id}.${index}`}
                semaphore={semaphore}
                onSave={(newSemaphore) => handleOnSave(newSemaphore, semaphore.name)}
                onDelete={() => {
                  const removed = application.semaphores?.filter((keep) => keep.id !== semaphore.id);
                  setApplication({
                    ...application,
                    semaphores: removed
                  });
                  setUpdateNodes(getUpdatedNodes(semaphore.id, 'semaphore_id', application.blocks.details));
                }}
              />
            );
          })}
      </List>
      {openEdit && (
        <SemaphoreListItemEdit
          onClose={() => setOpenEdit(false)}
          onSave={(newUserEvent) => handleOnSave(newUserEvent)}
        />
      )}
      <Button variant='contained' color='primary' sx={{ ml: 2 }} onClick={() => setOpenEdit(true)}>
        Add new
      </Button>
    </Stack>
  );
};

export default SemaphoreManager;
